import { useEffect, useState } from 'react';
import { facilitySearch } from '@aithora/shared';
import { useConsoleRole, useSession } from '../../session/SessionContext';
import { useCompanyWideView, useFacilityScope, usePickedIds } from '../../session/FacilityScope';
import { usePageTab } from '../../shell/Shell';
import { READ_ONLY_SHORT, useReadOnlyReason } from '../../ui/ReadOnly';
import { sx } from '../../ui/designStyles';
import { FacilityPopover } from '../capture/FacilityPopover';
import { AithoraTreasury } from './AithoraTreasury';
import { Billing } from './Billing';
import { FacilityTreasury } from './FacilityTreasury';
import { PrivatePay } from './PrivatePay';
import { pill, pre, seg, useFinanceApi } from './lib';

/** V9 adds Private pay (design fnTabs, L27113). */
type Tab = 'Treasury' | 'Billing' | 'Private pay';
const TABS: Tab[] = ['Treasury', 'Billing', 'Private pay'];
type Scope = 'Platform' | 'Facility';

/**
 * Finance (design isFinance, T10195-11032): Treasury and Billing, at Aithora scope and
 * Facility scope. Aithora's own money is Operator and Super Admin (the Partner may look;
 * the server decides); Facility scope is one building's money. A Facility Admin gets
 * only their own building, with no scope switch and no picker.
 */
export function FinancePage() {
  const { role, realRole } = useConsoleRole();
  const { session } = useSession();
  const roReason = useReadOnlyReason();
  const fa = role === 'ADMIN';
  const partner = role === 'PARTNER' || realRole === 'PARTNER';
  const op = !fa;
  // Aithora's own money (the treasury, GET /finance/aithora) is the operator tier's; the
  // server refuses the Partner, so the Partner gets Facility scope only (Andrew, 2026-10-02).
  // The Developer reads Finance, read only (2026-10-16), and the server refuses it Aithora's
  // own money too, so it also opens on Facility scope.
  const aithoraScope = op && !partner && role !== 'DEVELOPER';
  const operatorWrites = (role === 'SUPER_ADMIN' || role === 'OPERATOR') && roReason === null;
  const canAct = (operatorWrites || fa) && roReason === null;
  const [tab, setTab] = useState<Tab>('Treasury');
  const [scope, setScope] = useState<Scope>(aithoraScope ? 'Platform' : 'Facility');
  const [fac, setFac] = useState<{ id: string; name: string } | null>(null);
  const [pickOpen, setPickOpen] = useState(false);
  usePageTab(tab);

  // The building on the Facility scope: the Facility Admin's own, or (for Aithora) the
  // first the server's search returns until one is picked. The facility switcher (V6, design
  // fnFac) decides it while something is picked there: its building, or the first building
  // of the picked place; a building changed here holds until the switcher changes.
  const { inView } = useFacilityScope();
  const pickIds = usePickedIds();
  const pickKey = pickIds ? pickIds.join(',') : '';
  const first = useFinanceApi((c, signal) => facilitySearch(c, { query: { q: '', limit: 1 }, signal }), [], !pickIds);
  useEffect(() => {
    if (!pickIds) return;
    const f = inView.find((x) => pickIds.includes(x.id));
    setFac(f ? { id: f.id, name: f.name } : null);
  }, [pickKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const r = first.data?.rows[0];
    if (r && !fac && !pickIds) setFac({ id: r.id, name: r.name });
  }, [first.data, fac, pickIds]);
  // Finance › Aithora is the company's own money: company-wide whatever is picked.
  useCompanyWideView(aithoraScope && scope === 'Platform');
  const facName = fac?.name ?? '–';
  const facility = fa ? undefined : fac?.id;
  const isFac = scope === 'Facility';
  const access = partner ? 'View only' : fa ? 'Your building only' : roReason !== null ? READ_ONLY_SHORT : 'You can view and act';
  const scopes: [Scope, string, string][] = [
    ['Platform', 'Aithora', '#2F5C97'],
    ['Facility', 'Facility · ' + facName, '#1F6B52'],
  ];

  return (
    <div className="v9type" data-screen-label="Finance" data-testid="finance" style={sx('display:flex;flex-direction:column;gap:0;min-width:0;flex:1 1 auto;min-height:0;height:100%;overflow:hidden;')}>
      <div style={sx('flex:0 0 auto;display:flex;align-items:center;gap:7px;flex-wrap:wrap;row-gap:6px;margin-bottom:10px;')}>
        <span role="tablist" aria-label="Finance" style={sx('display:contents;')}>
          {TABS.map((n) => (
            <a
              key={n}
              href="#"
              role="tab"
              aria-selected={tab === n}
              onClick={(e) => {
                pre(e);
                setTab(n);
              }}
              style={sx(pill(tab === n))}
            >
              <span>{n}</span>
            </a>
          ))}
        </span>
        <span style={sx('flex:1;')} />
        <div role="group" aria-label="Scope" style={sx('flex:0 0 auto;white-space:nowrap;display:flex;align-items:center;gap:3px;flex-wrap:nowrap;border:1px solid rgba(34,39,46,.14);border-radius:8px;padding:2px;')}>
          {scopes
            .filter(([k]) => k === 'Facility' || aithoraScope)
            .map(([k, name, dot]) => (
              <a
                key={k}
                href="#"
                aria-pressed={scope === k}
                onClick={(e) => {
                  pre(e);
                  setScope(k);
                }}
                style={sx(seg(scope === k) + 'gap:7px;')}
              >
                <span style={sx('width:7px;height:7px;border-radius:50%;flex:0 0 7px;background:' + (scope === k ? '#fff' : dot) + ';')} />
                <span>{name}</span>
              </a>
            ))}
          {isFac && operatorWrites && (
            <span style={sx('position:relative;display:inline-flex;')}>
              <a
                href="#"
                title="Switch facility"
                aria-haspopup="dialog"
                aria-expanded={pickOpen}
                onClick={(e) => {
                  pre(e);
                  setPickOpen((o) => !o);
                }}
                style={sx('display:inline-flex;align-items:center;height:30px;padding:0 9px;border-radius:6px;font-size:13px;font-weight:700;color:#2F5C97;text-decoration:none;')}
              >
                Change {'▾'}
              </a>
              {pickOpen && (
                <FacilityPopover
                  variant="finance"
                  top={36}
                  current={fac}
                  onPick={(f) => {
                    if (f) {
                      setFac(f);
                      setScope('Facility');
                    } else setScope('Platform');
                    setPickOpen(false);
                  }}
                  onClose={() => setPickOpen(false)}
                />
              )}
            </span>
          )}
        </div>
        <span style={sx('display:inline-flex;align-items:center;gap:7px;font-size:13px;color:#54606C;flex:0 1 auto;min-width:0;')}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" aria-hidden="true">
            <rect x="5" y="11" width="14" height="10" rx="2" />
            <path d="M8.5 11V8a3.5 3.5 0 0 1 7 0v3" />
          </svg>
          <span>{access}</span>
        </span>
      </div>

      <div style={sx('flex:1 1 auto;min-height:0;overflow-y:auto;overflow-x:hidden;min-width:0;display:block;padding-right:3px;')}>
        {tab === 'Treasury' && !isFac && aithoraScope && <AithoraTreasury canAct={operatorWrites} readOnly={!operatorWrites} />}
        {tab === 'Treasury' && isFac && (fa || fac) && <FacilityTreasury facility={facility} facName={facName} canAct={canAct} />}
        {tab === 'Private pay' && <PrivatePay facName={fa ? session?.user.orgName ?? 'your building' : facName} canAct={canAct} />}
        {tab === 'Billing' && (fa || fac) && <Billing facility={fa ? session?.user.organizationId ?? undefined : facility} facName={fa ? session?.user.orgName ?? undefined : facName} canAct={canAct && !fa} desk={operatorWrites} readOnlyFacility={fa} roTitle={canAct && !fa ? undefined : fa ? 'Read only: Aithora submits claims as your billing agent.' : roReason ?? READ_ONLY_SHORT} />}
      </div>
    </div>
  );
}
